import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import NavBar from '../components/navBar/NavBar';
import SideBar from '../components/sideBar/SideBar';
import BottomTabBar from '../components/bottomTabBar/BottomTabBar';
import classNames from './layout.module.scss';
import { Outlet } from 'react-router';
import PageLoader from '@/components/pageLoader/PageLoader';
import useHideOnScroll from '@/hooks/useHideOnScroll';
import { PageHeaderProvider } from '@/context/PageHeaderContext';
import { BarsVisibilityProvider } from '@/context/BarsVisibilityContext';

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed';

const Layout = () => {
  const mainContentRef = useRef<HTMLDivElement>(null);
  const barsHidden = useHideOnScroll(mainContentRef);

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed));
    } catch {
      /* storage unavailable (private mode) — the state just won't persist */
    }
  }, [sidebarCollapsed]);

  const toggleSidebar = useCallback(() => setSidebarCollapsed((collapsed) => !collapsed), []);

  return (
    <PageHeaderProvider>
      <BarsVisibilityProvider hidden={barsHidden}>
        {/* data-sidebar drives `--sidebar-size`, which sizes the sidebar column and is read by
            descendants that need to sit next to it. */}
        <div
          className={classNames.container}
          data-sidebar={sidebarCollapsed ? 'collapsed' : 'expanded'}
        >
          <div className={classNames.NavBar} data-hidden={barsHidden || undefined}>
            <NavBar onToggleSidebar={toggleSidebar} sidebarCollapsed={sidebarCollapsed} />
          </div>
          <div className={classNames.SideBar}>
            <SideBar collapsed={sidebarCollapsed} />
          </div>
          <div
            id="main-container"
            ref={mainContentRef}
            className={`${classNames.mainContent} @container/main`}
          >
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </div>
          <BottomTabBar hidden={barsHidden} />
        </div>
      </BarsVisibilityProvider>
    </PageHeaderProvider>
  );
};
export default Layout;
